import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { useAuth } from '@/lib/auth'
import {
  useRegisterAccount,
  useSaveContractHours,
  useSavePerson,
  useSavePersonRate,
  useUpdateAccountEmail,
} from '@/lib/settings-api'
import type { PersonRateRow, PersonRow } from '@/lib/settings-types'
import type { UserRole } from '@/lib/types'
import { ROLE_LABEL, ROLE_SELECT_OPTIONS } from './shared'

export function AccountModal({ person, onClose }: { person: PersonRow; onClose: () => void }) {
  const register = useRegisterAccount()
  const [email, setEmail] = useState('')

  function submit() {
    if (!email.trim()) return
    register.mutate({ personId: person.id, input: { email: email.trim() } }, { onSuccess: onClose })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Account voor ${person.displayName}`}
      description={`Registreer het e-mailadres van het Microsoft-account. Daarmee kan deze persoon inloggen met de rol ${ROLE_LABEL[person.role]}; er is geen wachtwoord.`}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!email.trim() || register.isPending}>Registreren</button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">E-mail</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} className="field" type="email" placeholder="naam@configurewise.com" autoFocus />
        </label>
        <p className="text-xs text-ink-faint">
          Er gaat geen bericht naar deze persoon. Laat zelf weten dat de toegang klaarstaat op finance.configurewise.com.
        </p>
      </div>
    </Modal>
  )
}

/**
 * Het login-adres wijzigen kan alleen hier, in personenbeheer: buiten deze weg om
 * verzet niemand een adres, want dat is een login overnemen. Vandaar de
 * waarschuwing over de meldingen.
 */
export function AccountEmailModal({ person, current, onClose }: { person: PersonRow; current: string; onClose: () => void }) {
  const updateEmail = useUpdateAccountEmail()
  const [email, setEmail] = useState(current)
  const changed = email.trim() !== '' && email.trim().toLowerCase() !== current.toLowerCase()

  function submit() {
    if (!changed) return
    updateEmail.mutate({ personId: person.id, input: { email: email.trim() } }, { onSuccess: onClose })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`E-mailadres van ${person.displayName}`}
      description="Het adres van het Microsoft-account waarmee deze persoon inlogt."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!changed || updateEmail.isPending}>Opslaan</button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">E-mail</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} className="field" type="email" placeholder="naam@configurewise.com" autoFocus />
        </label>
        <p className="text-xs text-ink-faint">
          Het oude adres, het nieuwe adres en de beheerders krijgen hier bericht van. Deze persoon logt daarna in met het nieuwe adres.
        </p>
      </div>
    </Modal>
  )
}

const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/

export function PersonModal({ person, onClose }: { person: PersonRow | null; onClose: () => void }) {
  const auth = useAuth()
  const save = useSavePerson()
  const [displayName, setDisplayName] = useState(person?.displayName ?? '')
  const [initials, setInitials] = useState(person?.initials ?? '')
  const [role, setRole] = useState<UserRole>(person?.role ?? 'CONTRACTOR')
  const [aliases, setAliases] = useState((person?.nameAliases ?? []).join(', '))
  const [iban, setIban] = useState(person?.payoutDetails?.iban ?? '')
  const [accountHolderName, setAccountHolderName] = useState(person?.payoutDetails?.accountHolderName ?? '')

  // Uitbetaalgegevens wijzigen is beheerderswerk, dus de velden hangen aan die rol en
  // niet aan payoutDetails: dat is een lees-signaal (financiele toegang, dus ook een
  // partner) en zou een partner een invulveld geven waarop elke opslag een 403 wordt,
  // ook een simpele naamswijziging, want dit formulier stuurt altijd alle velden mee.
  const editsPayoutDetails = auth.data?.user.role === 'ADMIN'
  const normalizedIban = iban.trim().toUpperCase().replace(/\s+/g, '')
  const ibanValid = normalizedIban === '' || IBAN_PATTERN.test(normalizedIban)

  function submit() {
    if (!displayName.trim() || !ibanValid) return
    const nameAliases = aliases.split(',').map((a) => a.trim()).filter(Boolean)
    const fields = {
      displayName: displayName.trim(),
      initials: initials.trim() || null,
      role,
      nameAliases,
      ...(editsPayoutDetails
        ? { iban: normalizedIban || null, accountHolderName: accountHolderName.trim() || null }
        : {}),
    }
    if (person) {
      save.mutate({ id: person.id, update: fields }, { onSuccess: onClose })
    } else {
      save.mutate({ create: fields }, { onSuccess: onClose })
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={person ? 'Persoon bewerken' : 'Nieuwe persoon'}
      description="Naam, rol en aliassen voor de urenmatching, plus de rekening waarop deze persoon wordt uitbetaald."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!displayName.trim() || !ibanValid || save.isPending}>{person ? 'Opslaan' : 'Aanmaken'}</button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Weergavenaam</span>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="field" autoFocus />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Initialen</span>
            <input value={initials} onChange={(e) => setInitials(e.target.value)} className="field" placeholder="Bijv. RS" />
          </label>
          <div className="block">
            <span id="person-role-label" className="mb-1.5 block text-sm font-medium text-ink-soft">Rol</span>
            <Select
              value={role}
              onValueChange={(value) => setRole(value as UserRole)}
              options={ROLE_SELECT_OPTIONS}
              ariaLabelledby="person-role-label"
              className="w-full"
            />
          </div>
        </div>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Aliassen</span>
          <input value={aliases} onChange={(e) => setAliases(e.target.value)} className="field" placeholder="Komma-gescheiden, bijv. Bram de Vries" />
        </label>
        {editsPayoutDetails ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">IBAN</span>
              <input value={iban} onChange={(e) => setIban(e.target.value)} className="field font-mono" placeholder="NL00 BANK 0123 4567 89" />
              {!ibanValid ? <span className="mt-1 block text-xs text-status-overdue">Dit is geen geldige IBAN</span> : null}
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">Naamgeving</span>
              <input value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} className="field" placeholder="Tenaamstelling van de rekening" />
            </label>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

export function ContractHoursModal({ personId, personName, onClose }: { personId: string; personName: string; onClose: () => void }) {
  const save = useSaveContractHours()
  const [hoursPerWeek, setHoursPerWeek] = useState('')
  const [validFrom, setValidFrom] = useState('')

  function submit() {
    const normalized = hoursPerWeek.trim().replace(',', '.')
    if (!normalized || !validFrom) return
    save.mutate({ personId, input: { hoursPerWeek: normalized, validFrom } }, { onSuccess: onClose })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Contracturen toevoegen"
      description={`Nieuwe periode voor ${personName}. De lopende periode wordt automatisch afgesloten.`}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!hoursPerWeek.trim() || !validFrom || save.isPending}>Toevoegen</button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Contracturen per week</span>
          <input value={hoursPerWeek} onChange={(e) => setHoursPerWeek(e.target.value)} className="field num" inputMode="decimal" placeholder="Bijv. 36" autoFocus />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Geldig vanaf</span>
          <input value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="field" type="date" />
        </label>
      </div>
    </Modal>
  )
}

export function RateModal({ rate, lockedPerson, onClose }: { rate: PersonRateRow | null; lockedPerson: PersonRow; onClose: () => void }) {
  const save = useSavePersonRate()
  const [hourlyRate, setHourlyRate] = useState(rate?.hourlyRate ?? '')
  const [validFrom, setValidFrom] = useState(rate?.validFrom ?? '')
  const [validTo, setValidTo] = useState(rate?.validTo ?? '')

  function submit() {
    const normalizedRate = hourlyRate.trim().replace(',', '.')
    if (rate) {
      save.mutate({ id: rate.id, update: { hourlyRate: normalizedRate, validTo: validTo || null } }, { onSuccess: onClose })
    } else {
      if (!hourlyRate.trim() || !validFrom) return
      save.mutate({ create: { personId: lockedPerson.id, hourlyRate: normalizedRate, validFrom, validTo: validTo || null } }, { onSuccess: onClose })
    }
  }

  const invalid = rate ? !hourlyRate.trim() : !hourlyRate.trim() || !validFrom

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={rate ? 'Uitbetaaltarief bewerken' : 'Nieuw uitbetaaltarief'}
      description={
        rate
          ? `Pas het bedrag of de einddatum aan dat wij ${lockedPerson.displayName} per uur betalen.`
          : `Leg vast wat wij ${lockedPerson.displayName} per uur betalen.`
      }
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={invalid || save.isPending}>{rate ? 'Opslaan' : 'Toevoegen'}</button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Persoon</span>
          <input value={lockedPerson.displayName} className="field" disabled />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Uitbetaaltarief per uur €</span>
          <input value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} className="field num" inputMode="decimal" placeholder="110,00" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Geldig vanaf</span>
            {rate ? (
              <input value={validFrom} className="field" disabled />
            ) : (
              <input value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="field" type="date" />
            )}
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Geldig t/m</span>
            <input value={validTo} onChange={(e) => setValidTo(e.target.value)} className="field" type="date" />
          </label>
        </div>
      </div>
    </Modal>
  )
}
